import type {
    LabelledChoice,
    StatusMeta,
    Transition,
    WorkflowDefinition,
} from "./types";
import { collectProblems, countBySeverity } from "./validation";

/**
 * Every problem in the definition, in one place.
 *
 * All of these are silent today: the engine logs an unregistered action and moves on, a rule
 * with an unusable condition simply never applies, and a shadowed rule reads as live
 * configuration. Collecting them is most of the value of having an editor at all.
 */
export function ValidationOverview({
    definition,
    statuses,
    dataProviders,
    onSelectTransition,
}: {
    definition: WorkflowDefinition;
    statuses: StatusMeta[];
    dataProviders: LabelledChoice[];
    onSelectTransition: (transition: Transition) => void;
}) {
    const problems = collectProblems(definition, statuses, dataProviders);
    const { errors, warnings } = countBySeverity(problems);

    if (!problems.length) {
        return (
            <section className="aer-wf-section">
                <h3>Validation</h3>
                <p className="aer-wf-panel__empty">
                    No problems found in this definition.
                </p>
            </section>
        );
    }

    return (
        <section className="aer-wf-section">
            <h3>
                Validation{" "}
                <span className="aer-wf-counts">
                    {errors > 0 && (
                        <span className="aer-wf-badge aer-wf-badge--danger">
                            {errors} {errors === 1 ? "error" : "errors"}
                        </span>
                    )}
                    {warnings > 0 && (
                        <span className="aer-wf-badge aer-wf-badge--warn">
                            {warnings} {warnings === 1 ? "warning" : "warnings"}
                        </span>
                    )}
                </span>
            </h3>

            <ul className="aer-wf-problems">
                {problems.map((problem) => {
                    const transition = problem.transitionId
                        ? definition.transitions.find(
                              (item) => item.id === problem.transitionId,
                          )
                        : undefined;

                    return (
                        <li
                            className={`aer-wf-problem aer-wf-problem--${problem.severity}`}
                            key={`${problem.severity}-${problem.message}`}
                        >
                            {transition ? (
                                // Every problem that belongs to a transition links to it, so the
                                // list is a route to the fix rather than a report about it.
                                <button
                                    type="button"
                                    className="aer-wf-problem__link"
                                    onClick={() =>
                                        onSelectTransition(transition)
                                    }
                                >
                                    {problem.message}
                                </button>
                            ) : (
                                <span>{problem.message}</span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
